import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Box, Typography, Stack, useMediaQuery, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  CustomSectionTitle,
  CustomSummaryCard,
  CustomButton,
  CustomTable,
  MobileTable,
  CustomColumnDef,
  CardTab,
  CustomTextInput,
  CustomSingleSelect,
  CustomSelect,
  CustomModal,
  colors,
} from "@ippon/shared-components";
import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchExpenses,
  updateExpense,
  deleteExpense,
  type ExpenseRecord,
} from "@/store/expenseSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import {
  isChangedFromDefault,
  resolveSearchControls,
} from "@/components/ListToolbar/searchControls";
import { Can } from "@/components/auth/Can";
import { getApiErrorType, type ApiErrorType } from "@/utils/apiErrorType";
import { buildFiscalYearOptions } from "@/utils/fiscalYearOptions";
import { useStaleInitialLoadErrorToast } from "@/utils/initialLoadError";
import { useExpenseBudgetCategories } from "@/hooks/useExpenseBudgetCategories";
import ExpenseDetailDrawer from "./ExpenseDetailDrawer";
import ExpenseCreateDrawer from "./ExpenseCreateDrawer";
import {
  DEFAULT_PROPERTY_DETAIL_TAB_ID,
  PROPERTY_DETAIL_TAB_IDS,
  PROPERTY_DETAIL_TABS,
  parseTabId,
} from "@/pages/properties/Detail/tabConfig";
import dayjs from "dayjs";
import { TypeChip, fmt, EXPENSE_GROUP_FALLBACK } from "./shared";

const ALL_TAB_KEY = "all";

const TableEllipsisText: React.FC<{ text: string }> = ({ text }) => (
  <Box
    component="span"
    data-table-ellipsis
    sx={{
      display: "block",
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </Box>
);

const isExpenseManagementTab = (params: URLSearchParams): boolean =>
  parseTabId(
    params.get("tab"),
    PROPERTY_DETAIL_TABS,
    DEFAULT_PROPERTY_DETAIL_TAB_ID,
  ) === PROPERTY_DETAIL_TAB_IDS.MANAGEMENT;

const EXPENSE_STATUS_OPTIONS = [
  { label: "下書き", value: "下書き" },
  { label: "AI作成", value: "AI作成" },
  { label: "支払予定", value: "支払予定" },
  { label: "未払い", value: "未払い" },
  { label: "支払済", value: "支払済" },
  { label: "要確認", value: "要確認" },
  { label: "保留", value: "保留" },
  { label: "その他", value: "その他" },
] as const;

interface ExpenseListTabProps {
  propertyId: string;
  propertyName?: string;
}

interface ExpenseRow extends ExpenseRecord {
  id: string;
  category_label: string;
}

type PerPageForm = { perPage: number };

const PER_PAGE_OPTIONS = [
  { value: 10, label: "10件" },
  { value: 25, label: "25件" },
  { value: 50, label: "50件" },
  { value: 100, label: "100件" },
];

const EXPENSE_COLUMNS = (deps: {
  statusMap: Record<string, ExpenseRecord["status"]>;
  handleStatusChange: (id: string, next: ExpenseRecord["status"]) => void;
  setPreviewExpenseId: (id: string | null) => void;
}): CustomColumnDef[] => [
  {
    field: "category_label",
    headerName: "明細項目",
    flex: 1,
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
        <TableEllipsisText text={String(value || "")} />
      </Box>
    ),
  },
  {
    field: "name",
    headerName: "支出タイトル",
    flex: 1.4,
    sortable: true,
    renderCell: ({ row }) => {
      const r = row as ExpenseRow;
      // 工事由来 (source=repair) はタイトルクリックで支出詳細を開けるようにする。
      // loan 由来は支出詳細でなく融資画面で完結するので引き続き非リンク。
      const isLinked = r.source === "loan";
      return (
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              component="span"
              data-testid={`expense-name-link-${r.expense_id}`}
              onClick={(e) => {
                e.stopPropagation();
                if (isLinked) return;
                deps.setPreviewExpenseId(r.expense_id);
              }}
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "inherit",
                minWidth: 0,
                flex: 1,
                textDecoration: isLinked ? "none" : "underline",
                cursor: isLinked ? "default" : "pointer",
                "&:hover": isLinked
                  ? {}
                  : { color: "#0b9dbd", textDecoration: "underline" },
              }}
            >
              <TableEllipsisText text={r.name} />
            </Box>
          </Stack>
        </Box>
      );
    },
  },
  {
    field: "expense_type",
    headerName: "区分",
    flex: 1,
    sortable: true,
    renderCell: ({ row }) => (
      <TypeChip type={(row as ExpenseRow).expense_type} />
    ),
  },
  {
    field: "amount",
    headerName: "金額",
    flex: 1,
    sortable: true,
    renderCell: ({ value }) => (
      <Box
        sx={{
          minWidth: 0,
          width: "100%",
          fontSize: "14px",
          fontWeight: 400,
        }}
      >
        <TableEllipsisText text={fmt(Number(value || 0))} />
      </Box>
    ),
  },
  {
    field: "status",
    headerName: "ステータス",
    flex: 1,
    sortable: true,
    disableTooltip: true,
    renderCell: ({ row }) => {
      const r = row as ExpenseRow;
      return (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ width: "120px", minWidth: "120px", flexShrink: 0 }}
        >
          <CustomSelect
            key={`${r.expense_id}_${String(deps.statusMap[r.expense_id] || r.status)}`}
            name={`expense_status_${r.expense_id}`}
            options={EXPENSE_STATUS_OPTIONS as any}
            width="120px"
            XsWidth="120px"
            fontSize="14px"
            defaultValue={(deps.statusMap[r.expense_id] || r.status) as any}
            sx={{
              width: "120px",
              minWidth: "120px",
              flexShrink: 0,
            }}
            onChange={(v) =>
              deps.handleStatusChange(
                r.expense_id,
                v as ExpenseRecord["status"],
              )
            }
          />
        </Box>
      );
    },
  },
  {
    field: "date",
    headerName: "発生日",
    flex: 1,
    sortable: true,
    renderCell: ({ value }) => {
      const v = value ? String(value) : "";
      const d = v ? dayjs(v.length <= 10 ? `${v}T00:00:00` : v) : null;
      const text = d && d.isValid() ? d.format("YYYY/MM/DD") : "-";
      return (
        <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
          <TableEllipsisText text={text} />
        </Box>
      );
    },
  },
];

const ExpenseListTab: React.FC<ExpenseListTabProps> = ({
  propertyId,
  propertyName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const { list } = useSelector((s: RootState) => s.expenseMgmt);
  const expenses = list.data;

  // 支出カテゴリマスタ Single Source of Truth = BudgetCategory
  // (予実画面の科目行と完全一致させるため、`ClientMasters.expense_categories`
  // ではなく BudgetCategories テーブルだけを参照する)
  const { items: budgetCategories, loading: budgetCategoriesLoading } =
    useExpenseBudgetCategories();

  // category_id → expense_group マップ (全カテゴリから。system/custom 両対応)。
  // 収入の categoryRevenueGroup と対称。CardTab・絞り込みはこの group 単位。
  const rawBudgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );
  const categoryExpenseGroup = useMemo(() => {
    const map: Record<string, string> = {};
    (rawBudgetCategories as any[]).forEach((c) => {
      if (
        c &&
        (c.category_type === "expense" ||
          c.category_type === "expense_opex" ||
          c.category_type === "expense_capex") &&
        c.expense_group
      ) {
        map[c.category_id] = c.expense_group;
      }
    });
    return map;
  }, [rawBudgetCategories]);

  // expense → 所属カテゴリ(expense_group)。未設定は「その他」に寄せる。
  const expenseGroupOf = useCallback(
    (e: ExpenseRecord): string =>
      categoryExpenseGroup[(e as any).budget_category_id] ||
      EXPENSE_GROUP_FALLBACK,
    [categoryExpenseGroup],
  );

  // CardTab に出すカテゴリ一覧: ユーザー管理の expense_group のみ。
  const expenseGroups = useMemo(() => {
    return Array.from(new Set(Object.values(categoryExpenseGroup))).filter(
      Boolean,
    );
  }, [categoryExpenseGroup]);

  // CardTab はデフォルトで "all" を選択。値は expense_group (カテゴリ名)。
  // URL ?category=<group|all> と同期 (デフォルト "all" の時は URL から省略)
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => searchParams.get("category") || ALL_TAB_KEY,
  );

  const updateSelectedCategory = useCallback(
    (nextKey: string) => {
      setSelectedCategory(nextKey);
      setSearchParams(
        (prev) => {
          if (!isExpenseManagementTab(prev)) return prev;
          const next = new URLSearchParams(prev);
          if (nextKey === ALL_TAB_KEY) {
            next.delete("category");
          } else {
            next.set("category", nextKey);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [perPage, setPerPage] = useState(50);
  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });

  // CustomTextInput / CustomSingleSelect 用の useForm
  // 参考: Estimates/List/EstimateList.tsx の指定なしパターン (空文字 + placeholder)
  const {
    register: registerFilter,
    control: controlFilter,
    getValues: getFilterValues,
    reset: resetFilter,
    setValue: setFilterValue,
    watch: watchFilter,
    formState: { errors: filterErrors },
  } = useForm<{
    q: string;
    type: string;
    status: string;
    fiscalYear: string;
  }>({
    defaultValues: { q: "", type: "", status: "", fiscalYear: "" },
  });

  const handleSearch = () => {
    const v = getFilterValues();
    setSearchQuery(v.q || "");
    setTypeFilter(v.type || "");
    setStatusFilter(v.status || "");
    // 年度はセレクト変更時に即時 setSelectedFiscalYear するため、ここでは扱わない
  };

  const handleKeywordClear = () => {
    setFilterValue("q", "");
    setSearchQuery("");
  };

  const handleClearSearch = () => {
    resetFilter({
      q: "",
      type: "",
      status: "",
      fiscalYear: String(currentFiscalYear),
    });
    setSearchQuery("");
    setTypeFilter("");
    setStatusFilter("");
    fiscalYearUserTouchedRef.current = false;
    setSelectedFiscalYear(currentFiscalYear);
  };

  // 登録/編集ドロワー
  // Notion 風プレビュードロワー (詳細ページを埋め込み)
  const [previewExpenseId, setPreviewExpenseId] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // 新規作成プレビュードロワー (/expenses/new ページを埋め込み)
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  // 複数選択
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  // 行ごとのステータスをローカル管理 (修繕一覧と同様、楽観更新 → API 失敗時ロールバック)
  const [statusMap, setStatusMap] = useState<
    Record<string, ExpenseRecord["status"]>
  >({});
  const handleStatusChange = useCallback(
    async (id: string, nextStatus: ExpenseRecord["status"]) => {
      const previous = statusMap[id];
      if (previous === nextStatus) return;
      setStatusMap((m) => ({ ...m, [id]: nextStatus })); // optimistic
      try {
        await dispatch(
          updateExpense({
            expense_id: id,
            body: { status: nextStatus },
          }),
        ).unwrap();
        showToast({ message: "ステータスを更新しました", type: "success" });
      } catch {
        // rollback
        setStatusMap((m) => ({ ...m, [id]: previous }));
        showToast({ message: "ステータス更新に失敗しました", type: "error" });
      }
    },
    [dispatch, showToast, statusMap],
  );

  const executeBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(
        selectedIds.map((id) =>
          dispatch(deleteExpense({ expense_id: id })).unwrap(),
        ),
      );
      showToast({
        message: `${selectedIds.length}件の支出を削除しました`,
        type: "success",
      });
      setSelectedIds([]);
    } catch {
      showToast({ message: "一部の削除に失敗しました", type: "error" });
    } finally {
      setIsBulkDeleting(false);
      setOpenDeleteModal(false);
    }
  };

  /** 選択中タブ(expense_group)と expense のマッチング。 */
  const matchSelectedCategory = useCallback(
    (e: ExpenseRecord): boolean => {
      if (selectedCategory === ALL_TAB_KEY) return true;
      return expenseGroupOf(e) === selectedCategory;
    },
    [selectedCategory, expenseGroupOf],
  );

  /** カテゴリ(expense_group)別件数。タブ表示用 */
  const countForGroup = useCallback(
    (group: string): number =>
      expenses.filter((e) => expenseGroupOf(e) === group).length,
    [expenses, expenseGroupOf],
  );

  const filtered = useMemo(
    () =>
      expenses.filter((e) => {
        if (!matchSelectedCategory(e)) return false;
        if (statusFilter && e.status !== statusFilter) return false;
        if (typeFilter && e.expense_type !== typeFilter) return false;
        if (searchQuery && !e.name.includes(searchQuery)) return false;
        return true;
      }),
    [expenses, matchSelectedCategory, statusFilter, typeFilter, searchQuery],
  );

  // 会計年度: BE は決算月 (fiscal_closing_month) を返す。会計年度開始月はその翌月。
  // (Budget/index.tsx と同じロジックに揃える)
  const fiscalClosingMonth: number =
    useSelector(
      (state: RootState) =>
        (state.clients?.detailed?.data as { fiscal_closing_month?: number })
          ?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;
  const currentFiscalYear = useMemo(() => {
    const now = new Date();
    return now.getMonth() + 1 >= fiscalYearStartMonth
      ? now.getFullYear()
      : now.getFullYear() - 1;
  }, [fiscalYearStartMonth]);

  const { showKeywordClear, clearButton } = resolveSearchControls({
    keyword: watchFilter("q"),
    hasActiveFilters:
      !!(watchFilter("type") || watchFilter("status")) ||
      isChangedFromDefault(
        watchFilter("fiscalYear"),
        String(currentFiscalYear),
      ),
  });

  // 年度セレクト state: 初期値 = 今年度。ユーザーが手動切替で更新。
  const [selectedFiscalYear, setSelectedFiscalYear] =
    useState<number>(currentFiscalYear);
  // currentFiscalYear が後追いで確定したとき (fiscal_closing_month の遅延ロード) は
  // 初期同期する。ユーザーが既に手動選択していたら触らない。
  const fiscalYearUserTouchedRef = useRef(false);
  useEffect(() => {
    if (fiscalYearUserTouchedRef.current) return;
    setSelectedFiscalYear(currentFiscalYear);
    setFilterValue("fiscalYear", String(currentFiscalYear));
  }, [currentFiscalYear, setFilterValue]);

  const fiscalYearOptions = useMemo(() => {
    return buildFiscalYearOptions(currentFiscalYear);
  }, [currentFiscalYear]);

  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );
  const [summaryLoading, setSummaryLoading] = useState(!list.loaded);
  const expenseSummaryKey = `${propertyId}:${selectedFiscalYear}`;
  const [loadedSummaryKey, setLoadedSummaryKey] = useState<string | null>(null);
  const effectiveSummaryLoading =
    summaryLoading || loadedSummaryKey !== expenseSummaryKey;
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const requestKey = expenseSummaryKey;
    const fetchSeq = fetchSeqRef.current + 1;
    fetchSeqRef.current = fetchSeq;
    (async () => {
      setSummaryLoading(true);
      setInitialLoadError(null);
      try {
        const action = await dispatch(
          fetchExpenses({
            property_id: propertyId,
            fiscal_year: selectedFiscalYear,
          }),
        );
        if (cancelled) return;
        if ((action as { type?: string }).type?.endsWith("/rejected")) {
          const payload = (action as any).payload as
            | { errorType?: ApiErrorType }
            | undefined;
          setInitialLoadError(
            payload?.errorType ??
              getApiErrorType((action as any).error ?? action) ??
              "unknown",
          );
        }
      } catch (err) {
        if (!cancelled) setInitialLoadError(getApiErrorType(err));
      } finally {
        if (!cancelled && fetchSeqRef.current === fetchSeq) {
          setLoadedSummaryKey(requestKey);
          setSummaryLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, expenseSummaryKey, propertyId, selectedFiscalYear]);

  const hasExpensesCache = list.loaded || expenses.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasExpensesCache,
    dedupeKey: `expenses:${propertyId}:${selectedFiscalYear}`,
  });

  const summary = useMemo(() => {
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    // BE 側で年度フィルタ済みのため expenses は当該年度のみ。FE 集計も同年度で行う。
    const fyStart = new Date(selectedFiscalYear, fiscalYearStartMonth - 1, 1);
    const fyEnd = new Date(selectedFiscalYear + 1, fiscalYearStartMonth - 1, 1);
    const yearly = expenses.filter((e) => {
      const d = new Date(e.date);
      return d >= fyStart && d < fyEnd;
    });
    return {
      ytd: yearly.reduce((s, e) => s + e.amount, 0),
      capex: yearly
        .filter((e) => e.expense_type === "CAPEX")
        .reduce((s, e) => s + e.amount, 0),
      opex: yearly
        .filter((e) => e.expense_type === "OPEX")
        .reduce((s, e) => s + e.amount, 0),
      thisMonth: expenses
        .filter((e) => e.date.startsWith(monthPrefix))
        .reduce((s, e) => s + e.amount, 0),
      paid: expenses
        .filter((e) => e.status === "支払済")
        .reduce((s, e) => s + e.amount, 0),
      pending: expenses
        .filter((e) => e.status !== "支払済")
        .reduce((s, e) => s + e.amount, 0),
    };
  }, [expenses, selectedFiscalYear, fiscalYearStartMonth]);

  const tableRows = useMemo(
    () =>
      filtered.map((record) => {
        const matched = budgetCategories.find(
          (category) =>
            category.category_id === (record as any).budget_category_id,
        );
        return {
          ...record,
          id: record.expense_id,
          category_label: matched?.name || record.category || "",
        };
      }),
    [filtered, budgetCategories],
  );
  const tableColumns = useMemo(
    () =>
      EXPENSE_COLUMNS({
        statusMap,
        handleStatusChange,
        setPreviewExpenseId,
      }),
    [statusMap, handleStatusChange],
  );
  const tableResetKey = `${selectedCategory}|${searchQuery}|${statusFilter}|${typeFilter}|${perPage}`;
  const tableDataReady = !list.loading && !budgetCategoriesLoading;
  const desktopTable = useMemo(
    () => (
      <CustomTable
        rows={tableDataReady ? tableRows : []}
        columns={tableColumns}
        initialSort={{ field: "date", direction: "desc" }}
        enableCheckboxSelection
        onRowSelection={setSelectedIds}
        outlined={false}
        minHeight="auto"
        pageSize={perPage}
        pageResetKey={tableResetKey}
        isLoading={!tableDataReady}
        noDataMessage="支出が登録されていません"
        noDataIcon={
          <ReceiptIcon
            sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
          />
        }
      />
    ),
    [tableRows, tableColumns, perPage, tableResetKey, tableDataReady],
  );

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ mb: 0 }}>
        <CustomSectionTitle title="支出管理" />
      </Box>
      {/* 年度切替: セクションタイトル直下に独立配置。変更で即時 API fetch
          上 12px / 下 8px の余白で配置 */}
      <Box sx={{ mt: 1.5, mb: 1, width: 140 }}>
        <CustomSingleSelect
          label=""
          name="fiscalYear"
          control={controlFilter}
          errors={filterErrors}
          options={fiscalYearOptions}
          onChangeValue={(val) => {
            const yr = Number(val);
            if (!Number.isNaN(yr) && Number.isFinite(yr)) {
              fiscalYearUserTouchedRef.current = true;
              setSummaryLoading(true);
              setSelectedFiscalYear(yr);
            }
          }}
          labelWidth="0px"
          inputWidth="100%"
          inputWidthSp="100%"
          hasSpacing={false}
        />
      </Box>
      {/* サマリー */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          // 金額が長いカードだけ内容に追従して広がる (均等割りにしない)。
          // 各カードは内容幅、入り切らなければ次行へ折返す。
          gap: 1,
          mb: 1,
          width: "100%",
          "& > *": { flex: "0 0 auto", minWidth: "160px" },
          "& .summary-card-value": { whiteSpace: "nowrap" },
        }}
      >
        <CustomSummaryCard
          label="累計支出"
          value={fmt(summary.ytd ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="CAPEX"
          value={fmt(summary.capex ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="OPEX"
          value={fmt(summary.opex ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="当月支出"
          value={fmt(summary.thisMonth ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="支払済"
          value={fmt(summary.paid ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="支払予定"
          value={fmt(summary.pending ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
      </Box>
      {/* カテゴリフィルタ (CardTab) — expense_group(7カテゴリ+カスタム)で絞り込み。
          収入の revenue_group フィルタと対称。 */}
      <Box sx={{ mb: 1 }}>
        <CardTab
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
          tabs={[
            {
              key: ALL_TAB_KEY,
              label: "すべて",
              count: expenses.length,
              hasNotification: false,
            },
            ...expenseGroups.map((g) => ({
              key: g,
              label: g,
              count: countForGroup(g),
              hasNotification: false,
            })),
          ]}
          tabData={Object.fromEntries(
            [ALL_TAB_KEY, ...expenseGroups].map((key) => [
              key,
              { data: [], pagination: { total: 0, page: 1, limit: 50 } },
            ]),
          )}
          activeTab={(() => {
            if (selectedCategory === ALL_TAB_KEY) return 0;
            const idx = expenseGroups.findIndex((g) => g === selectedCategory);
            return idx < 0 ? 0 : idx + 1;
          })()}
          onTabChange={(idx) => {
            if (idx === 0) {
              updateSelectedCategory(ALL_TAB_KEY);
              return;
            }
            const g = expenseGroups[idx - 1];
            updateSelectedCategory(g || ALL_TAB_KEY);
          }}
        />
      </Box>

      {/* 検索・フィルタ・登録 */}
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          mb: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 240, flex: 1, maxWidth: 380 }}>
          <CustomTextInput
            label=""
            name="q"
            placeholder="支出タイトルで検索"
            register={registerFilter}
            errors={filterErrors}
            inputWidth="100%"
            inputWidthSp="100%"
            showSearchIconStart
            search
            hasSpacing={false}
            showClearButton={showKeywordClear}
            onClearClick={handleKeywordClear}
            clearButtonAriaLabel="キーワードをクリア"
          />
        </Box>
        <Box sx={{ minWidth: 140 }}>
          <CustomSingleSelect
            label=""
            name="type"
            placeholder="経費区分"
            control={controlFilter}
            errors={filterErrors}
            options={[
              { value: "CAPEX", label: "CAPEX" },
              { value: "OPEX", label: "OPEX" },
            ]}
            labelWidth="0px"
            inputWidth="100%"
            inputWidthSp="100%"
            hasSpacing={false}
            onChangeValue={(v) => setFilterValue("type", v as string)}
          />
        </Box>
        <Box sx={{ minWidth: 120 }}>
          <CustomSingleSelect
            label=""
            name="status"
            placeholder="ステータス"
            control={controlFilter}
            errors={filterErrors}
            options={EXPENSE_STATUS_OPTIONS as any}
            labelWidth="0px"
            inputWidth="100%"
            inputWidthSp="100%"
            hasSpacing={false}
            onChangeValue={(v) => setFilterValue("status", v as string)}
          />
        </Box>
        <CustomButton
          label="検索"
          buttonCategory="search"
          onClick={handleSearch}
        />
        <CustomButton
          label="クリア"
          buttonCategory="clear"
          onClick={handleClearSearch}
          sx={{
            visibility: clearButton === "reserved" ? "hidden" : "visible",
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" gap={1} alignItems="center">
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#3e3e3e",
              whiteSpace: "nowrap",
            }}
          >
            表示件数
          </Typography>
          <Box sx={{ minWidth: 70 }}>
            <CustomSingleSelect
              label=""
              name="perPage"
              control={perPageControl}
              errors={{}}
              options={PER_PAGE_OPTIONS}
              inputWidth="80px"
              inputWidthSp="100%"
              hasSpacing={false}
              onChangeValue={(value) => {
                const next = Number(value);
                if (Number.isFinite(next)) setPerPage(next);
              }}
            />
          </Box>
        </Stack>

        <Can action="expense:edit">
          <CustomButton
            label="支出登録"
            onClick={() => setCreateDrawerOpen(true)}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          />
        </Can>
        <Can action="expense:delete">
          <CustomButton
            label="削除"
            buttonCategory="delete"
            onClick={() => setOpenDeleteModal(true)}
            disabled={selectedIds.length === 0 || isBulkDeleting}
          />
        </Can>
      </Box>

      {/* 一覧テーブル (sortable) — カテゴリ先頭、アイコン非表示
          key にフィルタ条件を含めて、タブ/フィルタ切替時に内部 page state をリセット */}
      {isMobile ? (
        <MobileTable
          key={tableResetKey}
          rows={tableRows}
          columns={tableColumns}
          checkBoxOpen
          selectedIds={selectedIds}
          onRowSelection={setSelectedIds}
          pageSize={perPage}
          noDataMessage="支出が登録されていません"
          noDataIcon={
            <ReceiptIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
          }
          handlePropertyClick={(row) =>
            setPreviewExpenseId(String(row.expense_id ?? row.id))
          }
        />
      ) : (
        desktopTable
      )}

      {/* Notion 風 支出詳細プレビュードロワー */}
      <ExpenseDetailDrawer
        open={!!previewExpenseId}
        onClose={() => setPreviewExpenseId(null)}
        expenseId={previewExpenseId}
        propertyId={propertyId}
        propertyName={propertyName}
      />

      {/* Notion 風 支出新規作成プレビュードロワー */}
      <ExpenseCreateDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        propertyId={propertyId}
        presetBudgetCategoryId={undefined}
        onCreated={() => {
          dispatch(
            fetchExpenses({
              property_id: propertyId,
              fiscal_year: selectedFiscalYear,
            }),
          );
        }}
      />

      {/* 削除確認モーダル */}
      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedIds.length}件の支出を削除してもよろしいでしょうか？
            <br />
            予実管理の集計からも該当の金額が差し引かれます
            (内訳行は履歴として残ります)。
            <br />
            削除されたデータは復元できません。
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => setOpenDeleteModal(false)}
            buttonCategory="cancelModal"
          />
          <Can action="expense:delete">
            <CustomButton
              label="削除"
              onClick={executeBulkDelete}
              buttonCategory="deleteModal"
              disabled={isBulkDeleting}
            />
          </Can>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default ExpenseListTab;
